import { 
  collection, 
  addDoc,
  updateDoc, 
  deleteDoc, 
  doc, 
  getDoc, 
  getDocs, 
  query, 
  where, 
  orderBy, 
  serverTimestamp,
  QueryDocumentSnapshot,
  DocumentData,
  Timestamp,
  writeBatch
} from 'firebase/firestore';
import { db } from '../firebase';
import { Project } from '../types';

const COLLECTION_NAME = 'projects';
const DELETED_COLLECTION_NAME = 'deletedProjects';

// Create a new project
export const createProject = async (projectData: Omit<Project, 'id' | 'createdAt' | 'updatedAt'>) => {
  try {
    const docRef = await addDoc(collection(db, COLLECTION_NAME), {
      ...stripUndefined(projectData),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    
    return { id: docRef.id };
  } catch (error) {
    console.error('Error creating project:', error);
    throw error;
  }
};

// Get a project by ID
export const getProject = async (projectId: string) => {
  try {
    const docRef = doc(db, COLLECTION_NAME, projectId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() } as Project;
    } else {
      console.warn(`Project with ID ${projectId} not found`);
      return null;
    }
  } catch (error) {
    console.error('Error getting project:', error);
    throw error;
  }
};

// Get all projects
export const getAllProjects = async () => {
  try {
    const q = query(
      collection(db, COLLECTION_NAME),
      orderBy('updatedAt', 'desc')
    );
    
    const querySnapshot = await getDocs(q);
    const projects: Project[] = [];
    
    querySnapshot.forEach((doc: QueryDocumentSnapshot<DocumentData>) => {
      projects.push({ id: doc.id, ...doc.data() } as Project);
    });
    
    return projects;
  } catch (error) {
    console.error('Error getting projects:', error);
    throw error;
  }
};

/**
 * Recursively drop `undefined` values.
 *
 * Firestore rejects `undefined` outright ("Unsupported field value: undefined"),
 * and an optional field that was never written — e.g. `contact.email` on a
 * project imported from a calendar event — arrives here as undefined the first
 * time the form saves it. Stripping is the right call rather than writing null:
 * the field is genuinely absent, and null would make "no email" look like a
 * deliberately cleared value. Arrays are preserved as arrays.
 */
function stripUndefined<T>(value: T): T {
  if (Array.isArray(value)) {
    return value.map(v => stripUndefined(v)) as unknown as T;
  }
  // Timestamps, Dates and other class instances must pass through untouched.
  if (value === null || typeof value !== 'object' || value.constructor !== Object) {
    return value;
  }
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    if (v === undefined) continue;
    out[k] = stripUndefined(v);
  }
  return out as T;
}

// Update a project
export const updateProject = async (projectId: string, data: Partial<Project>) => {
  try {
    const docRef = doc(db, COLLECTION_NAME, projectId);
    await updateDoc(docRef, {
      ...stripUndefined(data),
      updatedAt: serverTimestamp()
    });
    
    return { id: projectId };
  } catch (error) {
    console.error('Error updating project:', error);
    throw error;
  }
};

// Soft delete a project (move to deleted collection) -- atomic batch write
export const deleteProject = async (projectId: string) => {
  try {
    const docRef = doc(db, COLLECTION_NAME, projectId);
    const docSnap = await getDoc(docRef);
    
    if (!docSnap.exists()) {
      throw new Error(`Project with ID ${projectId} not found`);
    }
    
    const projectData = { ...docSnap.data() };
    const deletedDocRef = doc(db, DELETED_COLLECTION_NAME, `${projectId}_${Date.now()}`);

    const batch = writeBatch(db);
    batch.set(deletedDocRef, {
      ...projectData,
      deletedAt: serverTimestamp(),
      expiresAt: Timestamp.fromDate(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)),
      originalId: projectId,
    });
    batch.delete(docRef);
    await batch.commit();
    
    return { id: projectId };
  } catch (error) {
    console.error('Error deleting project:', error);
    throw error;
  }
};

// Get recently deleted projects
export const getDeletedProjects = async () => {
  try {
    const q = query(
      collection(db, DELETED_COLLECTION_NAME),
      where('expiresAt', '>', Timestamp.now()),
      orderBy('expiresAt', 'desc')
    );
    
    const querySnapshot = await getDocs(q);
    const projects: Array<Project & { deletedAt: Timestamp, expiresAt: Timestamp }> = [];
    
    querySnapshot.forEach((doc: QueryDocumentSnapshot<DocumentData>) => {
      projects.push({ id: doc.id, ...doc.data() } as Project & { deletedAt: Timestamp, expiresAt: Timestamp });
    });
    
    return projects;
  } catch (error) {
    console.error('Error getting deleted projects:', error);
    throw error;
  }
};

// Restore a deleted project.
// `deletedDocId` is the id of the document in the deletedProjects collection,
// which is `${originalId}_${timestamp}` (see deleteProject). The project must be
// restored under its ORIGINAL id so that its rooms/items/photos (which reference
// the original projectId) stay linked.
export const restoreProject = async (deletedDocId: string) => {
  try {
    // Get the deleted project data
    const deletedRef = doc(db, DELETED_COLLECTION_NAME, deletedDocId);
    const deletedSnap = await getDoc(deletedRef);

    if (!deletedSnap.exists()) {
      throw new Error(`Deleted project with ID ${deletedDocId} not found`);
    }

    // Remove deletion metadata (and originalId, which is not part of the project doc)
    const projectData = deletedSnap.data();
    const { deletedAt, expiresAt, originalId, ...restData } = projectData;

    // Prefer the stored originalId. Fall back to stripping the `_timestamp` suffix
    // from the doc id for any legacy deleted docs written without originalId.
    const restoreId: string = originalId || deletedDocId.replace(/_\d+$/, '');
    const docRef = doc(db, COLLECTION_NAME, restoreId);

    const batch = writeBatch(db);
    batch.set(docRef, {
      ...restData,
      restoredAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    batch.delete(deletedRef);
    await batch.commit();

    return { id: restoreId };
  } catch (error) {
    console.error('Error restoring project:', error);
    throw error;
  }
};

// Permanently delete a project
export const permanentlyDeleteProject = async (projectId: string) => {
  try {
    const deletedRef = doc(db, DELETED_COLLECTION_NAME, projectId);
    await deleteDoc(deletedRef);
    
    return { id: projectId };
  } catch (error) {
    console.error('Error permanently deleting project:', error);
    throw error;
  }
};

// Get projects by status
export const getProjectsByStatus = async (status: Project['status']) => {
  try {
    const q = query(
      collection(db, COLLECTION_NAME),
      where('status', '==', status),
      orderBy('updatedAt', 'desc')
    );
    
    const querySnapshot = await getDocs(q);
    const projects: Project[] = [];
    
    querySnapshot.forEach((doc: QueryDocumentSnapshot<DocumentData>) => {
      projects.push({ id: doc.id, ...doc.data() } as Project);
    });
    
    return projects;
  } catch (error) {
    console.error('Error getting projects by status:', error);
    throw error;
  }
};

// Get counts for a project (rooms, items, photos, checklists)
export const getProjectCounts = async (projectId: string): Promise<{
  roomCount: number;
  itemCount: number;
  photoCount: number;
  checklistCount: number;
  damageReportCount: number;
}> => {
  try {
    // Query for rooms count
    const roomsQuery = query(
      collection(db, 'rooms'),
      where('projectId', '==', projectId)
    );
    const roomsSnapshot = await getDocs(roomsQuery);
    
    // Query for items count
    const itemsQuery = query(
      collection(db, 'items'),
      where('projectId', '==', projectId)
    );
    const itemsSnapshot = await getDocs(itemsQuery);
    
    // Query for photos count
    const photosQuery = query(
      collection(db, 'photos'),
      where('projectId', '==', projectId)
    );
    const photosSnapshot = await getDocs(photosQuery);
    
    // Query for checklists count
    const checklistsQuery = query(
      collection(db, 'checklists'),
      where('projectId', '==', projectId)
    );
    const checklistsSnapshot = await getDocs(checklistsQuery);
    
    // Query for damage reports count
    const damageReportsQuery = query(
      collection(db, 'damageReports'),
      where('projectId', '==', projectId)
    );
    const damageReportsSnapshot = await getDocs(damageReportsQuery);
    
    return {
      roomCount: roomsSnapshot.docs.length,
      itemCount: itemsSnapshot.docs.length,
      photoCount: photosSnapshot.docs.length,
      checklistCount: checklistsSnapshot.docs.length,
      damageReportCount: damageReportsSnapshot.docs.length
    };
  } catch (error) {
    console.error('Error getting project counts:', error);
    throw error;
  }
};

// Get active projects (more efficient than getAllProjects + filtering)
export const getActiveProjects = async () => {
  try {
    // Create a query that directly excludes 'avslutad' and 'avbruten' statuses
    const q = query(
      collection(db, COLLECTION_NAME),
      where('status', 'not-in', ['avslutad', 'avbruten']),
      orderBy('updatedAt', 'desc')
    );
    
    const querySnapshot = await getDocs(q);
    const projects: Project[] = [];
    
    querySnapshot.forEach((doc: QueryDocumentSnapshot<DocumentData>) => {
      projects.push({ id: doc.id, ...doc.data() } as Project);
    });
    
    return projects;
  } catch (error) {
    console.error('Error getting active projects:', error);
    throw error;
  }
};

// Get active projects with counts in a single function (more efficient)
export const getActiveProjectsWithCounts = async (): Promise<{
  projects: Project[], 
  counts: {
    [projectId: string]: {
      roomCount: number;
      itemCount: number;
      photoCount: number;
      checklistCount: number;
      damageReportCount: number;
    }
  }
}> => {
  try {
    // First, get all active projects
    const projects = await getActiveProjects();
    
    const collections = ['rooms', 'items', 'photos', 'checklists', 'damageReports'];
    const projectIds = projects.map(p => p.id);
    
    const counts: {[projectId: string]: any} = {};
    projectIds.forEach(id => {
      counts[id] = {
        roomCount: 0,
        itemCount: 0,
        photoCount: 0,
        checklistCount: 0,
        damageReportCount: 0
      };
    });
    
    if (projectIds.length === 0) {
      return { projects, counts };
    }

    // Firestore 'in' queries are limited to 10 items — batch accordingly
    const batchQueries: Promise<{ collectionName: string; snapshot: any }>[] = [];
    for (let i = 0; i < projectIds.length; i += 10) {
      const batchIds = projectIds.slice(i, i + 10);
      collections.forEach(collectionName => {
        const q = query(
          collection(db, collectionName),
          where('projectId', 'in', batchIds)
        );
        batchQueries.push(getDocs(q).then(snapshot => ({ collectionName, snapshot })));
      });
    }
    
    const results = await Promise.all(batchQueries);
    
    // Process the results
    results.forEach(({ collectionName, snapshot }) => {
      snapshot.docs.forEach((doc: QueryDocumentSnapshot<DocumentData>) => {
        const data = doc.data();
        if (data.projectId && counts[data.projectId]) {
          switch (collectionName) {
            case 'rooms':
              counts[data.projectId].roomCount++;
              break;
            case 'items':
              counts[data.projectId].itemCount++;
              break;
            case 'photos':
              counts[data.projectId].photoCount++;
              break;
            case 'checklists':
              counts[data.projectId].checklistCount++;
              break;
            case 'damageReports':
              counts[data.projectId].damageReportCount++;
              break;
          }
        }
      });
    });
    
    return { projects, counts };
  } catch (error) {
    console.error('Error getting active projects with counts:', error);
    throw error;
  }
};

// Get projects by filter with counts in a single function (optimized version)
export const getProjectsByFilterWithCounts = async (
  filter: 'active' | 'all' | 'needs_attention' | Project['status']
): Promise<{
  projects: Project[], 
  counts: {
    [projectId: string]: {
      roomCount: number;
      itemCount: number;
      photoCount: number;
      checklistCount: number;
      damageReportCount: number;
    }
  }
}> => {
  try {
    // Create the appropriate query based on the filter
    let projectQuery;
    const projectsRef = collection(db, COLLECTION_NAME);
    
    switch (filter) {
      case 'active':
        projectQuery = query(
          projectsRef,
          where('status', 'not-in', ['avslutad', 'avbruten']),
          orderBy('updatedAt', 'desc')
        );
        break;
      case 'all':
        projectQuery = query(
          projectsRef,
          orderBy('updatedAt', 'desc')
        );
        break;
      case 'needs_attention':
        // For needs_attention, we'll fetch all and filter later
        projectQuery = query(
          projectsRef,
          orderBy('updatedAt', 'desc')
        );
        break;
      default:
        // For status-based filters
        projectQuery = query(
          projectsRef,
          where('status', '==', filter),
          orderBy('updatedAt', 'desc')
        );
    }

    // Get projects
    const projectsSnapshot = await getDocs(projectQuery);
    let projects = projectsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as Project));

    const counts: {[projectId: string]: any} = {};
    const projectIds = projects.map(p => p.id);
    
    projectIds.forEach(id => {
      counts[id] = {
        roomCount: 0,
        itemCount: 0,
        photoCount: 0,
        checklistCount: 0,
        damageReportCount: 0
      };
    });

    if (projectIds.length === 0) {
      return { projects, counts };
    }

    const collections = ['rooms', 'items', 'photos', 'checklists', 'damageReports'];
    const batchPromises: Promise<any>[] = [];

    for (let i = 0; i < projectIds.length; i += 10) {
      const batchIds = projectIds.slice(i, i + 10);
      collections.forEach(collectionName => {
        const q = query(
          collection(db, collectionName),
          where('projectId', 'in', batchIds)
        );
        batchPromises.push(
          getDocs(q).then(snapshot => ({ collectionName, snapshot }))
        );
      });
    }

    const results = await Promise.all(batchPromises);

    // Process results
    results.forEach(({ collectionName, snapshot }) => {
      snapshot.docs.forEach((doc: QueryDocumentSnapshot<DocumentData>) => {
        const data = doc.data();
        if (data.projectId && counts[data.projectId]) {
          switch (collectionName) {
            case 'rooms':
              counts[data.projectId].roomCount++;
              break;
            case 'items':
              counts[data.projectId].itemCount++;
              break;
            case 'photos':
              counts[data.projectId].photoCount++;
              break;
            case 'checklists':
              counts[data.projectId].checklistCount++;
              break;
            case 'damageReports':
              counts[data.projectId].damageReportCount++;
              break;
          }
        }
      });
    });

    return { projects, counts };
  } catch (error) {
    console.error(`Error getting projects by filter '${filter}' with counts:`, error);
    throw error;
  }
};

// Migrate project status from English to Swedish
export const migrateProjectStatus = async (): Promise<boolean> => {
  try {
    const projects = await getAllProjects();
    
    for (const project of projects) {
      // Use type assertion to handle both English and Swedish statuses
      const status = project.status as string;
      let newStatus = status as Project['status'];
      
      // Handle English and uppercase Swedish statuses
      if (status === 'new' || status === 'HEM') {
        newStatus = 'hem';
      } else if (status === 'evaluated' || status === 'VÄRDERAD') {
        newStatus = 'värderad';
      } else if (status === 'scheduled' || status === 'PLANERAD') {
        newStatus = 'planerad';
      } else if (status === 'completed' || status === 'AVSLUTAD') {
        newStatus = 'avslutad';
      } else if (status === 'waiting' || status === 'VÄNTAR') {
        newStatus = 'väntar';
      } else if (status === 'cancelled' || status === 'AVBRUTEN') {
        newStatus = 'avbruten';
      }
      
      // Migrate project status if it changed
      if (status !== newStatus) {
        await updateProject(project.id, { status: newStatus });
      }
    }
    
    return true;
  } catch (error) {
    console.error('Error migrating project status:', error);
    return false;
  }
}; 